/**
 * Who the letter comes from, and what it says.
 *
 * The sender fields live here; everything below them is `LetterEditor`, shared
 * with the template screen. That split is the point: a template is a letter
 * without a sender, and a campaign is a letter with one, so the body editor has
 * no business existing twice.
 *
 * The sender fields default to empty on purpose. Filling them from the CRM's
 * transactional sender would put cold mail on the same domain as password
 * resets and invoices; the transport's own terms forbid the first, and a
 * reputation hit would take the second down with it.
 */
import { AtSign } from "lucide-react";
import { LETTER_TOKENS } from "@wa/core/letter-tokens";
import { Input } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { Campaign } from "../../../../lib/marketing";
import type { EmailDesign } from "../../../../emailBuilder";
import { LetterEditor } from "../letter/LetterEditor";
import { Field, Panel } from "../shared";

/**
 * The tokens the sender replaces per recipient. `unsubscribe` is the one with a
 * legal weight behind it; the rest are convenience.
 *
 * Imported rather than typed out again: the other half of this contract is in
 * the API, and a list that agreed with it today would drift the first time
 * either side gained a token. Same reasoning as `UNSUBSCRIBE_PLACEHOLDER`.
 */
const TOKENS = LETTER_TOKENS;

export function MessagePanel({
  campaign,
  onChange,
  disabled,
  siteId,
}: {
  campaign: Campaign;
  onChange: (p: Partial<Campaign>) => void;
  disabled?: boolean;
  siteId?: string | null;
}) {
  const t = useT();

  return (
    <div className="space-y-4">
      <Panel
        title={t("marketing.message.senderTitle")}
        description={t("marketing.message.senderIntro")}
        icon={<AtSign className="size-4" />}
      >
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
          <Field label={t("marketing.message.fromName")}>
            <Input
              value={campaign.fromName}
              onChange={(e) => onChange({ fromName: e.target.value })}
              disabled={disabled}
            />
          </Field>
          <Field label={t("marketing.message.fromAddress")} hint={t("marketing.message.fromAddressHint")}>
            <Input
              value={campaign.fromAddress}
              onChange={(e) => onChange({ fromAddress: e.target.value })}
              placeholder={t("common.example.personEmail")}
              disabled={disabled}
            />
          </Field>
          <Field label={t("marketing.message.replyTo")}>
            <Input
              value={campaign.replyTo}
              onChange={(e) => onChange({ replyTo: e.target.value })}
              disabled={disabled}
            />
          </Field>
        </div>
      </Panel>

      <LetterEditor
        subject={campaign.subject}
        preheader={campaign.preheader}
        bodyHtml={campaign.bodyHtml}
        bodyText={campaign.bodyText}
        design={campaign.design as EmailDesign | null}
        locale={campaign.locale === "en" ? "en" : "et"}
        fromName={campaign.fromName}
        siteId={siteId}
        disabled={disabled}
        onChange={(p) => onChange(p as Partial<Campaign>)}
      />

      <Panel title={t("marketing.message.tokensTitle")} description={t("marketing.message.tokensIntro")}>
        <ul className="grid gap-2 text-xs sm:grid-cols-2">
          {TOKENS.map((k) => (
            <li key={k} className="flex items-baseline gap-2">
              <code className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">{`{{${k}}}`}</code>
              <span className="text-muted-foreground">{t(`marketing.token.${k}`)}</span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}
